import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box, Typography, Paper, FormControl, InputLabel, Select, MenuItem, Button,
  TextField, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemText, IconButton, Grid,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useAuth } from "../context/AuthContext";
import {
  fetchPlayersForUser,
  fetchFeeds,
  fetchSchedulesForPlayer,
  createScheduleEntry,
  deleteScheduleEntry,
  ScheduleEntry,
} from "../api/api";
import PlayerSelector from "../components/Players/PlayerSelector";
import { useTranslation } from "react-i18next";

type Feed = {
  id: string;
  external_name: string;
};

const dayNames: { [key: number]: string } = {
  1: "monday",
  2: "tuesday",
  3: "wednesday",
  4: "thursday",
  5: "friday",
  6: "saturday",
  7: "sunday",
};

const Scheduling: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();

  const [selectedPlayerSN, setSelectedPlayerSN] = useState<string | null>(null);
  const [availableFeeds, setAvailableFeeds] = useState<Feed[]>([]);
  const [selectedFeedId, setSelectedFeedId] = useState<string | null>(null);
  const [selectedFeedName, setSelectedFeedName] = useState<string | null>(null);

  const [dayOfWeek, setDayOfWeek] = useState<number>(1);
  const [startTime, setStartTime] = useState<string>("08:00");

  const [schedules, setSchedules] = useState<ScheduleEntry[]>([]);
  const [snackbarMsg, setSnackbarMsg] = useState<string | null>(null);

  const [confirmDeleteScheduleDialogOpen, setConfirmDeleteScheduleDialogOpen] = useState(false);
  const [scheduleEntryToDelete, setScheduleEntryToDelete] = useState<ScheduleEntry | null>(null);

  const loadFeeds = useCallback(async () => {
    if (selectedPlayerSN) {
      try {
        const fetchedFeeds = await fetchFeeds(selectedPlayerSN);
        setAvailableFeeds(fetchedFeeds);
        if (selectedFeedId && !fetchedFeeds.some((f: Feed) => f.id === selectedFeedId)) {
          setSelectedFeedId(null);
          setSelectedFeedName(null);
        } else if (selectedFeedId) {
          const currentFeed = fetchedFeeds.find((f: Feed) => f.id === selectedFeedId);
          if (currentFeed) setSelectedFeedName(currentFeed.external_name);
        }
      } catch (error) {
        console.error("Error fetching feeds:", error);
        setSnackbarMsg(t("scheduling.errorLoadingFeeds"));
      }
    } else {
      setAvailableFeeds([]);
      setSelectedFeedId(null);
      setSelectedFeedName(null);
    }
  }, [selectedPlayerSN, selectedFeedId, t]);

  const loadSchedules = useCallback(async () => {
    if (selectedPlayerSN) {
      try {
        const fetchedSchedules = await fetchSchedulesForPlayer(selectedPlayerSN);
        const sortedSchedules = fetchedSchedules.sort((a: ScheduleEntry, b: ScheduleEntry) => {
          if (a.day_of_week !== b.day_of_week) {
            return a.day_of_week - b.day_of_week;
          }
          return a.start_time.localeCompare(b.start_time);
        });
        setSchedules(sortedSchedules);
      } catch (error) {
        console.error("Error fetching schedules:", error);
        setSnackbarMsg(t("scheduling.errorLoadingSchedules"));
      }
    } else {
      setSchedules([]);
    }
  }, [selectedPlayerSN, t]);

  useEffect(() => {
    loadFeeds();
  }, [loadFeeds]);

  useEffect(() => {
    loadSchedules();
  }, [loadSchedules]);

  const handlePlayerChange = (sn: string) => {
    setSelectedPlayerSN(sn);
  };

  const handleFeedChange = (event: any) => {
    const feedId = event.target.value as string;
    setSelectedFeedId(feedId);
    const feed = availableFeeds.find(f => f.id === feedId);
    setSelectedFeedName(feed ? feed.external_name : null);
  };

  const handleDayChange = (event: any) => {
    setDayOfWeek(Number(event.target.value));
  };

  const handleStartTimeChange = (event: any) => {
    const value = event.target.value;
    if (/^([01]\d|2[0-3]):([0-5]\d)$/.test(value) || value === "") {
      setStartTime(value);
    }
  };

  const handleAddSchedule = async () => {
    if (!selectedPlayerSN || !selectedFeedId || !selectedFeedName || !startTime) {
      setSnackbarMsg(t("scheduling.fillAllFields"));
      return;
    }

    const startTimeFull = startTime.length === 5 ? `${startTime}:00` : startTime;

    try {
      await createScheduleEntry(
        selectedPlayerSN,
        dayOfWeek,
        startTimeFull,
        selectedFeedId,
        selectedFeedName
      );
      setSnackbarMsg(t("scheduling.scheduleAdded"));
      loadSchedules();
    } catch (error: any) {
      // --- DEBUGGING START ---
      console.error("Error adding schedule entry:", error);
      console.log("Error status:", error.status);
      console.log("Error data:", error.data);
      console.log("Error data.error:", error.data ? error.data.error : 'N/A');
      // --- DEBUGGING END ---

      if (error.status === 409 && error.data && error.data.error === "duplicate_schedule_entry") {
        setSnackbarMsg(t("scheduling.duplicateScheduleError"));
      } else {
        setSnackbarMsg(t("scheduling.errorAddingSchedule"));
      }
    }
  };

  const handleDeleteScheduleClick = (entry: ScheduleEntry) => {
    setScheduleEntryToDelete(entry);
    setConfirmDeleteScheduleDialogOpen(true);
  };

  const handleConfirmDeleteSchedule = async () => {
    if (scheduleEntryToDelete && selectedPlayerSN) {
      try {
        await deleteScheduleEntry(selectedPlayerSN, scheduleEntryToDelete.id);
        setSnackbarMsg(t("scheduling.scheduleDeleted"));
        loadSchedules();
      } catch (error: any) {
        console.error("Error deleting schedule entry:", error);
        setSnackbarMsg(t("scheduling.errorDeletingSchedule"));
      }
    }
    setConfirmDeleteScheduleDialogOpen(false);
    setScheduleEntryToDelete(null);
  };

  const handleCancelDeleteSchedule = () => {
    setConfirmDeleteScheduleDialogOpen(false);
    setScheduleEntryToDelete(null);
  };

  const schedulesByDay = useMemo(() => {
    const grouped: { [key: number]: ScheduleEntry[] } = {};
    for (let i = 1; i <= 7; i++) {
      grouped[i] = [];
    }
    schedules.forEach((entry: ScheduleEntry) => {
      if (grouped[entry.day_of_week]) {
        grouped[entry.day_of_week].push(entry);
      }
    });
    Object.values(grouped).forEach(daySchedules => {
      daySchedules.sort((a: ScheduleEntry, b: ScheduleEntry) => a.start_time.localeCompare(b.start_time));
    });
    return grouped;
  }, [schedules]);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" sx={{ mb: 4 }}>
        {t("scheduling.title")}
      </Typography>

      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>{t("scheduling.playerSelection")}</Typography>
        <PlayerSelector
          userId={user?.id || null}
          value={selectedPlayerSN}
          onChange={handlePlayerChange}
          disabled={false}
        />
        {!selectedPlayerSN && (
          <Typography variant="body2" color="text.secondary">
            {t("scheduling.selectPlayerPrompt")}
          </Typography>
        )}
      </Paper>

      {selectedPlayerSN && (
        <Paper sx={{ p: 1.5, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 3 }}>{t("scheduling.addScheduleEntry")}</Typography>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel id="feed-select-label">{t("scheduling.selectFeed")}</InputLabel>
                <Select
                  labelId="feed-select-label"
                  value={selectedFeedId || ""}
                  label={t("scheduling.selectFeed")}
                  onChange={handleFeedChange}
                >
                  {availableFeeds.length === 0 ? (
                    <MenuItem disabled>{t("scheduling.noFeedsAvailable")}</MenuItem>
                  ) : (
                    availableFeeds.map((feed) => (
                      <MenuItem key={feed.id} value={feed.id}>
                        {feed.external_name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel id="day-select-label">{t("scheduling.selectDay")}</InputLabel>
                <Select
                  labelId="day-select-label"
                  value={dayOfWeek}
                  label={t("scheduling.selectDay")}
                  onChange={handleDayChange}
                >
                  {Object.entries(dayNames).map(([num, name]) => (
                    <MenuItem key={num} value={Number(num)}>
                      {t(`days.${name}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                label={t("scheduling.startTime")}
                type="time"
                value={startTime}
                onChange={handleStartTimeChange}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  step: 300, // 5 minutes
                }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddSchedule}
                fullWidth
                startIcon={<AddIcon />}
                disabled={!selectedFeedId || !startTime}
              >
                {t("scheduling.addEntry")}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {selectedPlayerSN && (
        <Paper sx={{ p: 1.5 }}>
          <Typography variant="h6" sx={{ mb: 3 }}>{t("scheduling.weeklySchedule")}</Typography>
          <Grid container spacing={1.5}>
            {Object.entries(schedulesByDay).map(([dayNum, entries]) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={dayNum}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    minHeight: 150,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Typography variant="subtitle1" gutterBottom>
                    {t(`days.${dayNames[Number(dayNum)]}`)}
                  </Typography>
                  {entries.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      {t("scheduling.noEntriesForDay")}
                    </Typography>
                  ) : (
                    <List
                      dense
                      sx={{
                        maxHeight: 240,
                        overflowY: 'auto',
                        flexGrow: 1,
                      }}
                    >
                      {entries.map((entry: ScheduleEntry) => (
                        <ListItem
                          key={entry.id}
                          secondaryAction={
                            <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteScheduleClick(entry)} size="small">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          }
                        >
                          <ListItemText
                            primary={`${entry.start_time.substring(0, 5)} - ${entry.feed_external_name}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      <Dialog
        open={confirmDeleteScheduleDialogOpen}
        onClose={handleCancelDeleteSchedule}
        aria-labelledby="confirm-delete-schedule-title"
        aria-describedby="confirm-delete-schedule-description"
      >
        <DialogTitle id="confirm-delete-schedule-title">{t("scheduling.confirmDeleteTitle")}</DialogTitle>
        <DialogContent>
          <Typography id="confirm-delete-schedule-description">
            {t("scheduling.confirmDeleteMessage", {
              feedName: scheduleEntryToDelete?.feed_external_name,
              day: t(`days.${dayNames[scheduleEntryToDelete?.day_of_week || 1]}`),
              time: scheduleEntryToDelete?.start_time.substring(0, 5),
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDeleteSchedule} color="primary">{t("common.cancel")}</Button>
          <Button onClick={handleConfirmDeleteSchedule} color="error" autoFocus>{t("common.delete")}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbarMsg}
        autoHideDuration={3000}
        onClose={() => setSnackbarMsg(null)}
        message={snackbarMsg}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      />
    </Box>
  );
};

export default Scheduling;
